import { ShieldAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { Skeleton } from '../../../../../shared/components/Skeleton';
import { SkeletonRegion } from '../../../../../shared/components/SkeletonRegion';
import { useTrailerDetail } from '../../trailers/hooks/use-trailer-detail';
import { useTrailerPermissions } from '../../trailers/hooks/use-trailer-permissions';
import type { TrailerDetail } from '../../trailers/trailers.api';
import type { FleetUnitVehicle } from '@chukua/common';
import { VehicleSection } from './VehicleSection';

export interface UnitTrailerParts {
  trailer: TrailerDetail;
  detail: ReturnType<typeof useTrailerDetail>;
  canManage: boolean;
  canManageDocuments: boolean;
}

interface UnitTrailerSectionProps {
  trailer: FleetUnitVehicle;
  children: (parts: UnitTrailerParts) => ReactNode;
}

/** The trailer's part of a unit tab: its record is read here, so the truck's page needs no second loading state. */
export function UnitTrailerSection({ trailer, children }: UnitTrailerSectionProps) {
  const detail = useTrailerDetail(trailer.id);
  const { canManage, canManageDocuments } = useTrailerPermissions();
  const { state } = detail;

  let body: ReactNode;
  if (state.status === 'loaded') body = children({ trailer: state.trailer, detail, canManage, canManageDocuments });
  else if (state.status === 'loading') {
    body = (
      <SkeletonRegion label={`Loading trailer ${trailer.registration}`} className="flex flex-col gap-2">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-4 w-2/3" />
      </SkeletonRegion>
    );
  } else
    body = <EmptyState icon={ShieldAlert} title="Couldn't load the trailer" description="Something went wrong loading this trailer. Try reloading the page." />;

  return (
    <VehicleSection vehicleKind="trailer" registration={trailer.registration}>
      {body}
    </VehicleSection>
  );
}
